import { useCallback, useEffect, useRef } from 'react';
import { useBoolean } from 'usehooks-ts';

import { useAuthHeaders } from '@pletly/feature-auth';
import { Props } from './types';

export const useFileLink = ({ url, filename, download = false }: Props) => {
	const { getHeaders } = useAuthHeaders();
	const linkRef = useRef<HTMLAnchorElement>(null);
	const { value: loading, setTrue: setLoading, setFalse: setNotLoading } = useBoolean(false);

	const handleAction = useCallback(async () => {
		setLoading();

		if (!linkRef.current || linkRef.current.href) {
			return setNotLoading();
		}

		try {
			const fileResult = await fetch(url, {
				headers: getHeaders(url),
			});
			if (fileResult.ok === false) {
				return;
			}

			const blob = await fileResult.blob();
			const href = URL.createObjectURL(blob);

			linkRef.current.href = href;
			linkRef.current.target = '_blank';
			linkRef.current.title = filename;
			if (download) {
				linkRef.current.download = filename;
			}

			return linkRef.current.click();
		} finally {
			setNotLoading();
		}
	}, [download, filename, getHeaders, setLoading, setNotLoading, url]);

	useEffect(() => {
		const currentLink = linkRef.current?.href;

		return () => {
			if (currentLink) {
				URL.revokeObjectURL(currentLink);
			}
		};
	}, []);

	return { linkRef, handleAction, loading };
};
